import { Flex, Select, Text } from '@hubertai/hubert-component-library';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TAvailableLanguageCodes } from '@hubertai/screening-question-types';
import RuleConditionByInterviewProperty from '../RuleConditionByInterviewProperty';
import RuleConditionByRandomNumber from '../RuleConditionByRandomNumber';
import { interviewPropertyShape } from '../helpers/ruleUpdates';
import type { TRuleEditorCommonProps } from '../types';
import InterviewPropertySelect from 'components/Selects/InterviewPropertySelect';
import type { TSelectBase } from 'types/general.types';
import { EConditionType } from 'types/rules.types';
import type { TQuestionRuleCondition } from 'types/rules.types';

interface Props extends TRuleEditorCommonProps {
  condition: TQuestionRuleCondition;
  conditionTypeOptions: TSelectBase[];
  onTypeChange: (type: EConditionType) => void;
  onChange: (patch: Partial<TQuestionRuleCondition>) => void;
  renderQuestionAnswer?: () => ReactNode;
  // See RuleConditionByInterviewProperty's own allowedLanguages prop — omit to show every language.
  interviewPropertyLanguages?: TAvailableLanguageCodes[];
}

const RuleConditionShowIf = ({
  condition,
  isReadOnly,
  conditionTypeOptions,
  onTypeChange,
  onChange,
  renderQuestionAnswer,
  interviewPropertyLanguages,
}: Props) => {
  const { t } = useTranslation('Templates');

  return (
    <Flex direction="column" gap="3">
      <Text size="2" weight="medium">
        {t('question.ruleConditions.showOnlyIf')}
      </Text>
      <Flex gap="2" wrap="wrap">
        <Select.Root
          value={condition.type}
          onValueChange={(value) => onTypeChange(value as EConditionType)}
          disabled={isReadOnly}
        >
          <Select.Trigger aria-label="Condition type" />
          <Select.Content>
            {conditionTypeOptions.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        {condition.type === EConditionType.InterviewProperty ? (
          <InterviewPropertySelect
            disabled={isReadOnly}
            selected={condition.property ?? ''}
            onSelect={(property) => onChange(interviewPropertyShape(property))}
          />
        ) : null}
      </Flex>

      {condition.type === EConditionType.QuestionAnswer ? (renderQuestionAnswer?.() ?? null) : null}

      {condition.type === EConditionType.InterviewProperty ? (
        <RuleConditionByInterviewProperty
          condition={condition}
          isReadOnly={isReadOnly}
          onChange={onChange}
          allowedLanguages={interviewPropertyLanguages}
        />
      ) : null}

      {condition.type === EConditionType.CandidateProperty ? <div className="candidate_property" /> : null}

      {condition.type === EConditionType.RandomNumber ? (
        <RuleConditionByRandomNumber condition={condition} isReadOnly={isReadOnly} onChange={onChange} />
      ) : null}
    </Flex>
  );
};

export default RuleConditionShowIf;
